import { RoutePath } from '../../constants/routePaths';
import { DCM_PAGES, ROUTE_PATH_TO_DCM_PAGE, SESSION_STORAGE_KEYS, SLOW_PAGE_THRESHOLDS_MS } from './constants';
import type { DcmPage } from './types';

const isCampaignScopedPath = (pathname: string): boolean =>
  pathname.includes(RoutePath.CAMPAIGNS) && !pathname.includes(RoutePath.LINE_ITEMS);

const CAMPAIGN_SUMMARY_AND_PERFORMANCE_PAGES: Array<{ matches: (pathname: string) => boolean; page: DcmPage }> = [
  {
    matches: (pathname) => pathname.includes(RoutePath.LINE_ITEMS) && pathname.includes(RoutePath.PERFORMANCE),
    page: DCM_PAGES.LINE_ITEM_PERFORMANCE,
  },
  {
    matches: (pathname) => pathname.includes(RoutePath.LINE_ITEMS) && pathname.includes(RoutePath.SUMMARY),
    page: DCM_PAGES.LINE_ITEM_SUMMARY,
  },
  {
    matches: (pathname) => isCampaignScopedPath(pathname) && pathname.includes(RoutePath.PERFORMANCE),
    page: DCM_PAGES.CAMPAIGN_PERFORMANCE,
  },
  {
    matches: (pathname) => isCampaignScopedPath(pathname) && pathname.includes(RoutePath.SUMMARY),
    page: DCM_PAGES.CAMPAIGN_SUMMARY,
  },
];

const CAMPAIGN_WORKFLOW_PAGES: Array<{ matches: (pathname: string) => boolean; page: DcmPage }> = [
  { matches: (pathname) => pathname.includes('/payment'), page: DCM_PAGES.CAMPAIGN_PAYMENT },
  { matches: (pathname) => pathname.includes('/review'), page: DCM_PAGES.CAMPAIGN_REVIEW },
  { matches: (pathname) => pathname.includes('/success'), page: DCM_PAGES.CAMPAIGN_SUCCESS },
  {
    matches: (pathname) => pathname.includes('/lineItem') && !pathname.includes('/lineItems'),
    page: DCM_PAGES.CAMPAIGN_BUILDER,
  },
];

const ROUTE_PATH_MATCH_ORDER = Object.values(RoutePath)
  .filter((routePath) => routePath !== RoutePath.HOME && routePath !== RoutePath.CAMPAIGNS)
  .sort((left, right) => right.length - left.length);

/** Collapse trailing slashes so route changes do not emit duplicate page sessions. */
export const normalizeAnalyticsPagePath = (pathname: string): string => {
  if (!pathname || pathname === '/') {
    return pathname || '/';
  }

  return pathname.replace(/\/+$/, '');
};

export const getPageFromPath = (pathname: string): DcmPage => {
  if (pathname === RoutePath.HOME) {
    return DCM_PAGES.HOME;
  }

  for (const { matches, page } of CAMPAIGN_SUMMARY_AND_PERFORMANCE_PAGES) {
    if (matches(pathname)) {
      return page;
    }
  }

  for (const routePath of ROUTE_PATH_MATCH_ORDER) {
    if (pathname.includes(routePath)) {
      return ROUTE_PATH_TO_DCM_PAGE[routePath];
    }
  }

  for (const { matches, page } of CAMPAIGN_WORKFLOW_PAGES) {
    if (matches(pathname)) {
      return page;
    }
  }

  if (pathname.includes(RoutePath.CAMPAIGNS)) {
    return ROUTE_PATH_TO_DCM_PAGE[RoutePath.CAMPAIGNS];
  }

  return DCM_PAGES.OTHER;
};

export const getDurationSince = (startMs: number): number => Math.max(0, Date.now() - startMs);

export const isSlowMetric = (metricName: string, value: number): boolean => {
  switch (metricName) {
    case 'LCP':
      return value >= SLOW_PAGE_THRESHOLDS_MS.LCP;
    case 'TTFB':
      return value >= SLOW_PAGE_THRESHOLDS_MS.TTFB;
    case 'FCP':
      return value >= SLOW_PAGE_THRESHOLDS_MS.FCP;
    case 'FID':
      return value >= SLOW_PAGE_THRESHOLDS_MS.FID;
    default:
      return false;
  }
};

export const readLoginFlowStartMs = (): number | null => {
  const raw = sessionStorage.getItem(SESSION_STORAGE_KEYS.LOGIN_FLOW_START);
  if (!raw) return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

export const markLoginFlowStarted = (): void => {
  sessionStorage.setItem(SESSION_STORAGE_KEYS.LOGIN_FLOW_START, String(Date.now()));
};

export const clearLoginFlowMarkers = (): void => {
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.LOGIN_FLOW_START);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.LOGIN_FLOW_HAD_MFA);
};

export const markLoginFlowHadMfa = (): void => {
  sessionStorage.setItem(SESSION_STORAGE_KEYS.LOGIN_FLOW_HAD_MFA, 'true');
};

export const hadLoginFlowMfa = (): boolean =>
  sessionStorage.getItem(SESSION_STORAGE_KEYS.LOGIN_FLOW_HAD_MFA) === 'true';
